import { isObject } from 'core/utils/object-utils';

/**
 * Core BackgroundContainer component props
 */
export interface BackgroundContainerProps {
	bgPadding?: string;
	bgColor?: string;
	bgImage?: string;
	bgImageInlineAltText?: string;
	bgImageInline?: boolean;
	bgImageShadeColor?: string;
	bgImageShadeOpacity?: number;
	bgImagePosition?: string;
	textColor?: 'light' | 'dark';
}

/**
 * Map of JSS prop names corresponding to this module,
 * and their component-prop name equivlanets.
 */
const JSSpropsMap: { [key: string]: string } = {
	backgroundColour: 'bgColor',
	// ! TODO - what about new definitions for these?
	// - also can the knobs helper draw from some central/exported definition const here?
	BackgroundImage: 'bgImage',
	BackgroundImagePosition: 'bgImagePosition',
	ImageOverlayColour: 'bgImageShadeColor',
	Opacity: 'bgImageShadeOpacity',
	TextColour: 'textColor',
};

/**
 * Sitecore format of the BackgroundContainer props, used for
 * extending interfaces of other components. Usage example:
 *
 * 		import { JSSprops as IBgProps } from 'pods/theme/components/BackgroundContainer';
 *
 * 		interface IProps {
 * 			fields: IBGProps & {
 * 				...
 * 			};
 * 		}
 */
export interface JSSprops {
	backgroundColour?: {
		value: BackgroundContainerProps['bgColor'];
	};
	BackgroundImage?: {
		value: {
			src: BackgroundContainerProps['bgImage'];
		};
	};
	BackgroundImagePosition?: {
		value: BackgroundContainerProps['bgImagePosition'];
	};
	ImageOverlayColour?: {
		value: BackgroundContainerProps['bgImageShadeColor'];
	};
	Opacity?: {
		value: BackgroundContainerProps['bgImageShadeOpacity'];
	};
	TextColour?: {
		value: BackgroundContainerProps['textColor'];
	};
}

/**
 * Extracts all relevant BackgroundContainer JSS props
 * from a provided props `fields` object, and returns just those
 * props appropriately renamed from their JSS equivalents.
 *
 * @param {Object} fields - The instigating module's `props.fields` object.
 * @returns {Object} Only relevant BackgroundContainer props, appropriately renamed.
 */
export const getBgProps = (fields: JSSprops): BackgroundContainerProps | {} =>
	fields
		? Object.keys(fields).reduce(
				(acc: BackgroundContainerProps, key: string) => {
					const matchingKey: string = JSSpropsMap[key];
					const value = fields?.[key as keyof JSSprops]?.value as any;

					if (matchingKey && value) {
						if (isObject(value)) {
							// BackgroundImage has two edge-cases; firstly its value
							// is nested inside an object as 'src' which we must account for.
							// secondly Sitecore never returns a 'null' image, it will instead
							// give us a default image; however we can discern if this is the
							// case by checking the adjacent `class` key, if it's === 'scEmptyImage'
							// in which case we can override the default image with null.
							if (matchingKey === 'bgImage') {
								acc[matchingKey] =
									value?.class === 'scEmptyImage' ? null : value?.src;
							}
						} else {
							acc[matchingKey as keyof BackgroundContainerProps] =
								typeof value === 'string' ? value.toLowerCase() : value;
						}
					}

					return acc;
				},
				{},
		  )
		: {};
